// Supply-chain pathway timeline — the centerpiece of the Chronology page:
// API ORIGIN → COUNTRY OF ORIGIN → US LABELLER, with an agreement-toned accent,
// optional caveat row, and the operator note as footer. TSX port of the mockup.
import React from 'react';
import { C, MONO, SERIF, AGREEMENT, TIER_COLOR } from './chronologyTokens';
import { Flag } from './chronologyFlags';
import { ChronoModel } from './chronologyAdapters';

interface NodeSpec {
  role: string;
  iso: string | null;
  country: string;
  big: string;
  bigSub: string;
  method: string;
  methodColor: string;
  caption: string;
}

function PathwayNode({ node }: { node: NodeSpec }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '6px 4px' }}>
      <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: C.mute, letterSpacing: '0.14em' }}>{node.role}</span>
      <Flag iso={node.iso} />
      <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: '-0.01em', lineHeight: 1.1, textAlign: 'center' }}>{node.country}</div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: C.ink, lineHeight: 1, textAlign: 'center' }}>{node.big}</div>
        {node.bigSub && <div style={{ fontFamily: MONO, fontSize: 10, color: C.dim }}>{node.bigSub}</div>}
      </div>
      <div style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: node.methodColor, letterSpacing: '0.08em', textAlign: 'center' }}>{node.method}</div>
      <div style={{ fontSize: 11, color: C.mute, textAlign: 'center', lineHeight: 1.45, maxWidth: 220, marginTop: 2 }}>{node.caption}</div>
    </div>
  );
}

function PathwayArrow({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', gap: 6, paddingTop: 42 }}>
      <svg width="48" height="20" viewBox="0 0 48 20">
        <line x1="2" y1="10" x2="40" y2="10" stroke={C.ink} strokeWidth="2" strokeDasharray="4 3" style={{ animation: 'chrDashFlow 1.2s linear infinite' }} />
        <path d="M36 4 L46 10 L36 16" fill="none" stroke={C.ink} strokeWidth="2" />
      </svg>
      <span style={{ fontFamily: MONO, fontSize: 8.5, fontWeight: 600, color: C.dim, letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}

function cleanLabeler(name?: string): string {
  if (!name) return '—';
  return name.replace(/,?\s+(Inc|LLC|Pharmaceuticals|Ltd|Limited|Co)\.?$/i, '').trim() || name;
}

// First sentence of the explanation, truncated on a word boundary (the full
// text is repeated verbatim in the operator note below).
function shortCaption(text?: string, max = 150): string {
  const s = ((text || '').split(/\.\s+/)[0] || '').trim();
  if (!s) return 'API-origin signal.';
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return cut.slice(0, lastSpace > 40 ? lastSpace : max).trim() + '…';
}

export default function PathwayTimeline({ model }: { model: ChronoModel }) {
  const [open, setOpen] = React.useState(true);
  const { api_origin, coo, product, narrative } = model;
  const a = AGREEMENT[api_origin.agreement];
  const p = api_origin.primary;

  // API ORIGIN node
  const apiNode: NodeSpec = p
    ? {
        role: 'API ORIGIN',
        iso: p.iso,
        country: p.country,
        big: p.share_pct != null ? `${p.share_pct.toFixed(2)} %` : (p.tier || p.case || '—'),
        bigSub: p.share_pct != null ? `by ${p.share_basis || 'share'}` : (p.case ? `case ${p.case}` : ''),
        method: p.method,
        methodColor: p.mfrLevel ? C.ink : C.gold,
        caption: shortCaption(p.explanation),
      }
    : {
        role: 'API ORIGIN',
        iso: null,
        country: 'Unknown',
        big: 'NO DATA',
        bigSub: '',
        method: 'NO SIGNAL',
        methodColor: C.dim,
        caption: 'No API-origin signal available for this molecule.',
      };

  // COUNTRY OF ORIGIN node
  const cooNode: NodeSpec = {
    role: 'COUNTRY OF ORIGIN',
    iso: coo.iso,
    country: coo.country_of_origin || 'Unknown',
    big: coo.tier || '—',
    bigSub: '',
    method: (coo.source || 'FDA / SPL').toUpperCase(),
    methodColor: C.ink,
    caption: coo.country_of_origin ? `Finished drug manufactured in ${coo.country_of_origin}.` : 'Country of origin not established.',
  };

  // US LABELLER node
  const labellerNode: NodeSpec = {
    role: 'US LABELLER',
    iso: 'US',
    country: 'United States',
    big: cleanLabeler(product.labeler),
    bigSub: '',
    method: 'FDA NDC',
    methodColor: C.ink,
    caption: product.manufacturer.parent
      ? `Parent fabricator: ${product.manufacturer.parent}`
      : (product.manufacturer.profile_note || 'US labeller of the marketed NDC.'),
  };

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ height: 3, background: a.color }} />
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={{ all: 'unset', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 22px', background: open ? C.soft : C.bg, borderBottom: open ? `1px solid ${C.line}` : 'none', transition: 'background 0.15s', boxSizing: 'border-box' }}>
        <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: C.ink, letterSpacing: '0.14em' }}>SUPPLY CHAIN PATHWAY</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 6, background: a.bg, border: `1px solid ${a.border}` }}>
          <span style={{ color: a.color, fontSize: 11, fontWeight: 700 }}>{a.icon}</span>
          <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: a.color, letterSpacing: '0.1em' }}>{a.label}</span>
        </span>
        <span style={{ fontSize: 12, color: C.mute }}>{api_origin.badge}</span>
        <span style={{ flex: 1 }} />
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" style={{ color: C.mute, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s ease', flexShrink: 0 }}>
          <path d="M3 5 L7 9 L11 5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <>
          <div style={{ padding: '22px', display: 'grid', gridTemplateColumns: '1fr 60px 1fr 60px 1fr', gap: 0, alignItems: 'stretch', animation: 'chrSlideUp 0.2s ease' }}>
            <PathwayNode node={apiNode} />
            <PathwayArrow label="FORMULATED IN" />
            <PathwayNode node={cooNode} />
            <PathwayArrow label="LABELLED BY" />
            <PathwayNode node={labellerNode} />
          </div>

          {api_origin.caveat && (
            <div style={{ padding: '10px 22px', background: 'rgba(212,160,23,0.08)', borderTop: '1px solid rgba(212,160,23,0.25)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: C.gold, letterSpacing: '0.14em', flexShrink: 0, marginTop: 2 }}>CAVEAT</span>
              <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.5 }}>{api_origin.caveat}</div>
            </div>
          )}

          {narrative && (
            <div style={{ padding: '12px 22px', background: C.soft, borderTop: `1px solid ${C.line}`, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: C.mute, letterSpacing: '0.14em', flexShrink: 0, paddingTop: 2 }}>OPERATOR NOTE</span>
              <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.55, fontStyle: 'italic' }}>{narrative}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
